import React, { useCallback, useContext, useEffect, useState } from 'react';
import { css } from '@emotion/css';
import { useForm } from 'react-hook-form';
import appLabels from '../../utils/appLabels';
import { get, has, invoke, debounce, includes, find } from 'lodash';
import { PreferencesContext, PreferencesContextType } from './PreferencesContext';
import PropTypes from 'prop-types';
import { useApp } from '../../context/app-context';
import { isApple } from '../../utils/utils';

interface IFormInput {
  placeType: string;
  streetAddress: string;
  businessName: string;
  aptSuite: string;
  city: string;
  state: string;
  zipCode: string;
}

const generalStyle = css`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  width: 57rem;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.3125rem;
  gap: 0.5rem;

  @media (max-width: 641px) {
    width: auto;
  }
`;

const warningBannerStyle = css`
  width: 57rem !important;

  @media (max-width: 641px) {
    width: auto !important;
  }
`;

const formFieldsStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;
`;

const radioStyle = css`
  accent-color: #7b1a27;
  width: 1.5rem;
  height: 1.5rem;
  vertical-align: middle;
  cursor: pointer;
`;
const radioRowStyle = css`
  display: flex;
  flex-direction: row;
  margin-bottom: 0.3rem;
`;

const labelStyle = css`
  margin-left: 0.5rem;
  margin-right: 2rem;
  font-size: 0.875rem;
  line-height: 1.3125rem;
`;

const inputTextStyle = css`
  display: flex;
  height: 3.5rem;
  padding: 0 1rem;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 0.5rem;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #495057 !important;
`;

const stateRowStyle = css`
  display: flex;
  flex-direction: row;
`;

const selectStyle = css`
  flex: 1;
  margin-left: 0.5rem;
`;

const cityStyle = css`
  flex: 1;
  flex-basis: 60%;

  @media (max-width: 641px) {
    flex-basis: 55%;
  }
`;

const counterStyle = css`
  margin-top: -0.5rem;
  font-size: 0.75rem;
  color: #535250;
`;

const fieldRequiredStyle = css`
  font-size: 0.7rem;
  line-height: 1rem;
  color: #7b1a27;
  border: 1px solid #d0001c !important;
  outline-color: #d0001c;
`;

const footerSectionStyle = css`
  display: flex;
  flex-direction: row-reverse;
  margin: 1.5rem 0;

  @media (max-width: 641px) {
    margin: 0;
  }
`;

const submitButtonStyle = css`
  input {
    width: 14.625rem;
    height: 3rem;
    border-radius: 6.25rem;
    background: #7b1a27;
    color: #fff;
    border: none;
  }

  input[disabled] {
    color: #535250;
    background: #f1f0ee;
  }
`;

const backButtonStyle = css`
  width: 14.625rem;
  height: 3rem;
  border-radius: 6.25rem;
  background: #fff;
  color: #7b1a27;
  border: 0.0625rem solid #7b1a27;
  margin-right: 1rem;
`;

const validationErrorStyle = css`
  color: #dd1e25;
  margin: 0 0.5rem;
`;

const ErrorMessage = ({ errors, name, labels }) => {
  const fieldError = get(errors, `${name}.type`);

  if (has(errors, name)) {
    return (
      <span id={`${name}_errormessage`} className={`${validationErrorStyle} body-text-xs`}>
        {get(labels, fieldError, fieldError)}
      </span>
    );
  } else {
    return null;
  }
};

const states: string[] = [
  'AL',
  'AK',
  'AS',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FM',
  'FL',
  'GA',
  'GU',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MH',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'MP',
  'OH',
  'OK',
  'OR',
  'PW',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VI',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

const DeliveryTabForm = ({ children, onBack, onSubmitForm, prefilledAddressValues, validationResponse }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    resetField,
    getFieldState,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormInput>({
    mode: 'onChange',
    defaultValues: {
      placeType: get(prefilledAddressValues, 'addressType.description', appLabels.DeliveryTabForm.residential),
      streetAddress: get(prefilledAddressValues, 'address1', ''),
      aptSuite: get(prefilledAddressValues, 'address2', ''),
      city: get(prefilledAddressValues, 'city', ''),
      state: get(prefilledAddressValues, 'state', ''),
      zipCode: get(prefilledAddressValues, 'zipCode', ''),
    },
  });
  const { setGlobalAriaLiveMessage } = useApp();
  const fieldNames = ['placeType', 'businessName', 'streetAddress', 'aptSuite', 'city', 'state', 'zipCode'];
  const firstError = find(fieldNames, (name) => !!get(errors, name));
  const firstErrorType = get(errors, `${firstError}.type`);

  useEffect(() => {
    if (firstError) {
      setGlobalAriaLiveMessage(`Error in new delivery address form: ${get(errors, `${firstError}.message`)}`);
    }
  }, [firstError, firstErrorType]);

  const isBusinessSelected = watch('placeType') === appLabels.DeliveryTabForm.business;
  const preferenceContext = useContext(PreferencesContext) as PreferencesContextType;

  const [isLoading, setIsLoading] = useState(false);
  const [showAddressLine2Warning, setShowAddressLine2Warning] = useState(false);

  // Check for incomplete address validation codes
  useEffect(() => {
    console.log('DeliveryTabForm validationResponse changed:', validationResponse);
    if (validationResponse?.length) {
      console.log('Found validationStatuses:', validationResponse);
      const hasIncompleteAddress = validationResponse.some(
        (status) => {
          const match = status.code === 'CCVS5039';
          console.log('Checking status:', status.code, 'matches:', match);
          return match;
        }
      );
      console.log('hasIncompleteAddress:', hasIncompleteAddress);
      if (hasIncompleteAddress) {
      

            console.log('Showing address line 2 warning');
        setShowAddressLine2Warning(true);
        setTimeout(() => {
          const aptSuiteInput = document.querySelector('input[name="aptSuite"]') as HTMLInputElement;
          if (aptSuiteInput) {
            aptSuiteInput.focus();
          }
        }, 100);
      }
    } else {
      console.log('No validationStatuses found in response');
    }
  }, [validationResponse]);

  const loadCity = useCallback(
    debounce(async () => {
      const zipcode = watch('zipCode');

      // only clearing city and state when zipcode is valid but not a recognized state
      if (/^\d{5}$/.test(zipcode)) {
        // set loading true so that the user can't click the button while we fetch the new city information
        setIsLoading(true);
        try {
          const resp = await invoke(preferenceContext, 'fetchCityFromZipCode', zipcode);

          const city = get(resp.data, 'geo.address2', '');
          const state = get(resp.data, 'geo.region', '');

          if (includes(states, state)) {
            setValue('city', city, { shouldValidate: true });
            setValue('state', state, { shouldValidate: true });
          } else {
            resetField('city');
            resetField('state');
          }
        } catch (err) {
          // not touching input fields in case of error
        } finally {
          setIsLoading(false);
        }
      }
    }, 300),
    [],
  );

  const submitFormData = async (data) => {
    if (isLoading) return;

    // resetting the form state, so we know when it's touched again after showing an error, to re-enable the save continue
    const currentData: IFormInput = watch();

    reset(currentData, { keepValues: true });
    
    // Call the submit function - validation response will trigger useEffect
    await onSubmitForm(data);
  };

  const appleDevice = isApple();

  const shouldDisableContinue = !isDirty && !showAddressLine2Warning && !validationResponse?.length;

  return (
    <>
      <div id="deliveryAddressline2Warning"
        className={`pds-message-bar pds-message-bar__accent pds-message-bar--warning rounded-lg ${!showAddressLine2Warning ? 'd-none' : ''} ${warningBannerStyle}`}
        role="alert"
      >
        <div className="d-flex">
          <div className="mr-2">
            <span className="pds-svg-warning-icon" aria-hidden="true"></span>
          </div>
          <div className="pds-message-bar__body d-flex flex-column">
            <h4 className="text-l-b font-weight-bold">Let’s keep your delivery on track</h4>
            <p className="text-m m-0">
              Seems like the address you entered may need an apartment, unit, or suite number. Incomplete addresses may
              lead to delivery delays or lost orders.
            </p>
          </div>
          <button
            className="pds-icon-btn pds-icon-btn-size_small pds-icon-btn-transparent--neutral d-flex align-items-center justify-content-center ml-auto pds-message-bar__close"
            aria-label="Close"
            onClick={() => setShowAddressLine2Warning(false)}
            type="button"
          >
            <span className="pds-svg-close pds-icon-btn-transparent--neutral__icon" aria-hidden="true"></span>
          </button>
        </div>
      </div>

      <form
        className={`item-list-container ${generalStyle}`}
        onSubmit={handleSubmit(submitFormData)}
        data-testid="deliveryAddressForm"
      >
        <div className={`item-list-container ${formFieldsStyle}`}>
          <div className={radioRowStyle}>
            <input
              className={radioStyle}
              type="radio"
              aria-label={appLabels.DeliveryTabForm.residential}
              value={appLabels.DeliveryTabForm.residential}
              {...register('placeType', { required: true })}
            />
            <label className={labelStyle}>{appLabels.DeliveryTabForm.residential}</label>

            <input
              className={radioStyle}
              type="radio"
              aria-label={appLabels.DeliveryTabForm.business}
              value={appLabels.DeliveryTabForm.business}
              {...register('placeType', { required: true })}
            />
            <label className={labelStyle}>{appLabels.DeliveryTabForm.business}</label>
          </div>

          {isBusinessSelected ? (
            <div>
              <input
                className={`${inputTextStyle} ${errors.businessName ? fieldRequiredStyle : ''}`}
                type="text"
                placeholder={appLabels.DeliveryTabForm.businessName}
                {...(appleDevice
                  ? { 'aria-label': get(errors, `businessName.message`) }
                  : {
                      'aria-invalid': get(getFieldState('businessName'), 'invalid'),
                    })}
                aria-errormessage={get(errors, `businessName.message`)}
                {...register('businessName', {
                  required: appLabels.DeliveryTabForm.businessNameRequired,
                  minLength: {
                    value: 2,
                    message: appLabels.DeliveryTabForm.businessNameMinLength,
                  },
                })}
              />
              <ErrorMessage
                errors={errors}
                name="businessName"
                labels={{
                  required: appLabels.DeliveryTabForm.businessNameRequired,
                  minLength: appLabels.DeliveryTabForm.businessNameMinLength,
                }}
              />
            </div>
          ) : null}

          <div>
            <input
              className={`${inputTextStyle} ${errors.streetAddress ? fieldRequiredStyle : ''}`}
              type="text"
              placeholder={appLabels.DeliveryTabForm.address}
              {...(appleDevice
                ? { 'aria-label': get(errors, `streetAddress.message`) }
                : {
                    'aria-invalid': get(getFieldState('streetAddress'), 'invalid'),
                  })}
              aria-errormessage={get(errors, `streetAddress.message`)}
              {...register('streetAddress', {
                required: appLabels.DeliveryTabForm.addressRequired,
              })}
            />
            <ErrorMessage
              errors={errors}
              name="streetAddress"
              labels={{
                required: appLabels.DeliveryTabForm.addressRequired,
              }}
            />
          </div>
          <input
            className={inputTextStyle}
            type="text"
            placeholder={appLabels.DeliveryTabForm.aptSuite}
            {...register('aptSuite', {})}
          />
          <div className={stateRowStyle}>
            <div className={cityStyle}>
              <input
                className={`${inputTextStyle} ${errors.city ? fieldRequiredStyle : ''}`}
                type="text"
                placeholder={appLabels.DeliveryTabForm.city}
                {...(appleDevice
                  ? { 'aria-label': get(errors, `city.message`) }
                  : { 'aria-invalid': get(getFieldState('city'), 'invalid') })}
                aria-errormessage={get(errors, `city.message`)}
                {...register('city', {
                  required: appLabels.DeliveryTabForm.cityRequired,
                })}
              />
              <ErrorMessage
                errors={errors}
                name="city"
                labels={{
                  required: appLabels.DeliveryTabForm.cityRequired,
                }}
              />
            </div>
            <div className={selectStyle} data-testid="state-dropdown">
              <select
                className={`${inputTextStyle} ${errors.state ? fieldRequiredStyle : ''}`}
                {...(appleDevice
                  ? { 'aria-label': get(errors, `state.message`) }
                  : { 'aria-invalid': get(getFieldState('state'), 'invalid') })}
                aria-errormessage={get(errors, `state.message`)}
                {...register('state', {
                  required: appLabels.DeliveryTabForm.stateRequired,
                })}
              >
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              <ErrorMessage
                errors={errors}
                name="state"
                labels={{
                  required: appLabels.DeliveryTabForm.stateRequired,
                }}
              />
            </div>
          </div>

          <div>
            <input
              className={`${inputTextStyle} ${errors.zipCode ? fieldRequiredStyle : ''}`}
              type="text"
              placeholder={appLabels.DeliveryTabForm.zipCode}
              {...(appleDevice
                ? { 'aria-label': get(errors, `zipCode.message`) }
                : { 'aria-invalid': get(getFieldState('zipCode'), 'invalid') })}
              aria-errormessage={get(errors, `zipCode.message`)}
              maxLength={5}
              {...register('zipCode', {
                required: appLabels.DeliveryTabForm.zipCodeRequired,
                pattern: {
                  value: /^\d+$/,
                  message: appLabels.DeliveryTabForm.zipCodePattern,
                },
                maxLength: 5,
                validate: (value) => {
                  //using this instead of minLength so regexp takes precedence
                  return get(value, 'length') >= 5 || appLabels.DeliveryTabForm.zipCodeMinLength;
                },
                onChange: loadCity,
              })}
            />
            <ErrorMessage
              errors={errors}
              name="zipCode"
              labels={{
                required: appLabels.DeliveryTabForm.zipCodeRequired,
                pattern: appLabels.DeliveryTabForm.zipCodePattern,
                validate: appLabels.DeliveryTabForm.zipCodeMinLength,
              }}
            />
          </div>

          {children}
        </div>

        <div className={footerSectionStyle}>
          <div className={submitButtonStyle}>
            <input type="submit" value="Continue" disabled={shouldDisableContinue} aria-label="Continue" />
          </div>
          <input className={backButtonStyle} type="submit" value="Back" onClick={onBack} aria-label="Back" />
        </div>
      </form>
    </>
  );
};

DeliveryTabForm.propTypes = {
  children: PropTypes.any,
  onBack: PropTypes.func,
  onSubmitForm: PropTypes.func,
  prefilledAddressValues: PropTypes.object,
  validationResponse: PropTypes.object,
};

export default DeliveryTabForm;
